import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, Search, X, ChevronLeft, ChevronRight, MoreVertical, Star } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { paymentMethodMasterApi, currencyMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { PaymentMethodListItem, CreatePaymentMethodRequest, UpdatePaymentMethodRequest, PaymentMethodType } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const SCREEN_CODE = 'PAYMENT_METHODS' as const
const ALL_STATUSES = '__all__'
const ALL_TYPES = '__all__'

const PAYMENT_METHOD_TYPES: { value: PaymentMethodType; label: string }[] = [
  { value: 'BANK', label: 'Bank Transfer' },
  { value: 'GATEWAY', label: 'Payment Gateway' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CRYPTO', label: 'Cryptocurrency' },
  { value: 'OTHER', label: 'Other' },
]

interface PaymentMethodFilters {
  searchTerm: string
  status: string
  type: string
}

export default function PaymentMethodMasterPage() {
  const { t } = useTranslation()
  const { canAddEdit: canAddEditFn } = usePermissions()
  const canAddEdit = canAddEditFn(SCREEN_CODE)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingMethod, setEditingMethod] = useState<PaymentMethodListItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filters, setFilters] = useState<PaymentMethodFilters>({
    searchTerm: '',
    status: ALL_STATUSES,
    type: ALL_TYPES,
  })
  const [formData, setFormData] = useState<CreatePaymentMethodRequest>({
    method_name: '',
    method_type: 'BANK',
    forex_markup_percent: 0,
    fixed_fee_amount: 0,
    fixed_fee_currency: 'USD',
    is_default: false,
  })

  const queryClient = useQueryClient()

  const { data: methods = [], isLoading } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => paymentMethodMasterApi.getAll(),
  })

  const { data: currencies = [] } = useQuery({
    queryKey: ['currencies', true],
    queryFn: () => currencyMasterApi.getAll(true),
  })

  // Filter methods based on filters
  const filteredMethods = useMemo(() => {
    return methods.filter(method => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        const matchesName = method.method_name.toLowerCase().includes(searchLower)
        if (!matchesName) return false
      }
      if (filters.status !== ALL_STATUSES) {
        const isActive = filters.status === 'active'
        if (method.is_active !== isActive) return false
      }
      if (filters.type !== ALL_TYPES) {
        if (method.method_type !== filters.type) return false
      }
      return true
    })
  }, [methods, filters])

  // Pagination calculations
  const totalRecords = filteredMethods.length
  const totalPages = Math.ceil(totalRecords / pageSize)
  const paginatedMethods = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredMethods.slice(startIndex, startIndex + pageSize)
  }, [filteredMethods, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const createMutation = useMutation({
    mutationFn: (data: CreatePaymentMethodRequest) => paymentMethodMasterApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      toast.success(t('masters.paymentMethods.createSuccess', 'Payment method created successfully'))
      handleCloseDialog()
    },
    onError: () => {
      toast.error(t('masters.paymentMethods.createError', 'Failed to create payment method'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePaymentMethodRequest }) =>
      paymentMethodMasterApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      toast.success(t('masters.paymentMethods.updateSuccess', 'Payment method updated successfully'))
      handleCloseDialog()
    },
    onError: () => {
      toast.error(t('masters.paymentMethods.updateError', 'Failed to update payment method'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => paymentMethodMasterApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      toast.success(t('masters.paymentMethods.deleteSuccess', 'Payment method deleted successfully'))
    },
    onError: () => {
      toast.error(t('masters.paymentMethods.deleteError', 'Failed to delete payment method'))
    },
  })

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => paymentMethodMasterApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      toast.success(t('masters.paymentMethods.setDefaultSuccess', 'Default payment method updated'))
    },
    onError: () => {
      toast.error(t('masters.paymentMethods.setDefaultError', 'Failed to set default payment method'))
    },
  })

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingMethod(null)
    setFormData({
      method_name: '',
      method_type: 'BANK',
      forex_markup_percent: 0,
      fixed_fee_amount: 0,
      fixed_fee_currency: 'USD',
      is_default: false,
    })
  }

  const handleEdit = (method: PaymentMethodListItem) => {
    setEditingMethod(method)
    setFormData({
      method_name: method.method_name,
      method_type: method.method_type,
      forex_markup_percent: method.forex_markup_percent,
      fixed_fee_amount: method.fixed_fee_amount,
      fixed_fee_currency: method.fixed_fee_currency,
      is_default: method.is_default,
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingMethod) {
      updateMutation.mutate({
        id: editingMethod.method_id,
        data: formData as UpdatePaymentMethodRequest,
      })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = (id: string) => {
    if (window.confirm(t('masters.paymentMethods.deleteConfirm', 'Are you sure you want to delete this payment method?'))) {
      deleteMutation.mutate(id)
    }
  }

  const handleToggleActive = (method: PaymentMethodListItem) => {
    updateMutation.mutate({
      id: method.method_id,
      data: { is_active: !method.is_active },
    })
  }

  const handleSetDefault = (id: string) => {
    setDefaultMutation.mutate(id)
  }

  const getMethodTypeLabel = (type: PaymentMethodType) => {
    return PAYMENT_METHOD_TYPES.find(t => t.value === type)?.label || type
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('masters.paymentMethods.title', 'Payment Methods')}
          </h1>
          <p className="text-muted-foreground">
            {t('masters.paymentMethods.description', 'Manage payment methods with forex markup configuration')}
          </p>
        </div>
        {canAddEdit && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t('masters.paymentMethods.add', 'Add Payment Method')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>
                    {editingMethod
                      ? t('masters.paymentMethods.edit', 'Edit Payment Method')
                      : t('masters.paymentMethods.add', 'Add Payment Method')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('masters.paymentMethods.formDescription', 'Configure payment method with forex charges')}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="method_name">{t('masters.paymentMethods.name', 'Method Name')} *</Label>
                    <Input
                      id="method_name"
                      value={formData.method_name}
                      onChange={(e) => setFormData({ ...formData, method_name: e.target.value })}
                      placeholder={t('masters.paymentMethods.namePlaceholder', 'e.g., Wire Transfer')}
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="method_type">{t('masters.paymentMethods.type', 'Method Type')} *</Label>
                    <Select
                      value={formData.method_type}
                      onValueChange={(value: PaymentMethodType) => setFormData({ ...formData, method_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHOD_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="forex_markup_percent">
                        {t('masters.paymentMethods.forexMarkup', 'Forex Markup %')}
                      </Label>
                      <Input
                        id="forex_markup_percent"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.forex_markup_percent}
                        onChange={(e) => setFormData({ ...formData, forex_markup_percent: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="fixed_fee_amount">
                        {t('masters.paymentMethods.fixedFee', 'Fixed Fee')}
                      </Label>
                      <Input
                        id="fixed_fee_amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.fixed_fee_amount}
                        onChange={(e) => setFormData({ ...formData, fixed_fee_amount: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="fixed_fee_currency">
                      {t('masters.paymentMethods.feeCurrency', 'Fee Currency')}
                    </Label>
                    <Select
                      value={formData.fixed_fee_currency}
                      onValueChange={(value) => setFormData({ ...formData, fixed_fee_currency: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((currency) => (
                          <SelectItem key={currency.currency_id} value={currency.currency_code}>
                            {currency.currency_code} - {currency.currency_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                    {editingMethod ? t('common.save', 'Save') : t('common.create', 'Create')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('masters.paymentMethods.search', 'Search payment methods...')}
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  className="pl-9"
                />
                {filters.searchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    onClick={() => setFilters({ ...filters, searchTerm: '' })}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder={t('common.status', 'Status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATUSES}>{t('common.allStatuses', 'All Statuses')}</SelectItem>
                <SelectItem value="active">{t('common.active', 'Active')}</SelectItem>
                <SelectItem value="inactive">{t('common.inactive', 'Inactive')}</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.type}
              onValueChange={(value) => setFilters({ ...filters, type: value })}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('masters.paymentMethods.type', 'Type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_TYPES}>{t('common.allTypes', 'All Types')}</SelectItem>
                {PAYMENT_METHOD_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="h-[calc(100vh-380px)] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>{t('masters.paymentMethods.name', 'Method Name')}</TableHead>
                  <TableHead>{t('masters.paymentMethods.type', 'Type')}</TableHead>
                  <TableHead className="text-right">{t('masters.paymentMethods.forexMarkup', 'Forex Markup')}</TableHead>
                  <TableHead className="text-right">{t('masters.paymentMethods.fixedFee', 'Fixed Fee')}</TableHead>
                  <TableHead>{t('common.status', 'Status')}</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      {t('common.loading', 'Loading...')}
                    </TableCell>
                  </TableRow>
                ) : paginatedMethods.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {t('masters.paymentMethods.noData', 'No payment methods found')}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedMethods.map((method) => (
                    <TableRow key={method.method_id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {method.method_name}
                          {method.is_default && (
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{getMethodTypeLabel(method.method_type)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{method.forex_markup_percent}%</TableCell>
                      <TableCell className="text-right">
                        {method.fixed_fee_amount > 0
                          ? `${method.fixed_fee_currency} ${method.fixed_fee_amount.toFixed(2)}`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={method.is_active ? 'default' : 'secondary'}>
                          {method.is_active ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {canAddEdit && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(method)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                {t('common.edit', 'Edit')}
                              </DropdownMenuItem>
                              {!method.is_default && (
                                <DropdownMenuItem onClick={() => handleSetDefault(method.method_id)}>
                                  <Star className="mr-2 h-4 w-4" />
                                  {t('masters.paymentMethods.setDefault', 'Set as Default')}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleToggleActive(method)}>
                                {method.is_active
                                  ? t('common.deactivate', 'Deactivate')
                                  : t('common.activate', 'Activate')}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDelete(method.method_id)}
                                className="text-destructive"
                                disabled={method.is_default}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {t('common.delete', 'Delete')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{t('common.rowsPerPage', 'Rows per page')}:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(parseInt(value))
              setCurrentPage(1)
            }}
          >
            <SelectTrigger className="w-[70px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>
            {startRecord}-{endRecord} {t('common.of', 'of')} {totalRecords}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">
            {t('common.page', 'Page')} {currentPage} {t('common.of', 'of')} {totalPages || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
